<!-- @format -->
<template>
    <div class="subscription-plan-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Edit Subscription Plan</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="loading" />
                <div v-else class="form-wrapper">
                    <div class="input-container">
                        <app-input
                            v-model="subscriptionPlan.name"
                            label="Name"
                            placeholder="Freemium"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="subscriptionPlan.basePrice"
                            label="Base Price"
                            placeholder="0"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="subscriptionPlan.billingCycle"
                            label="Billing Cycle in Months"
                            placeholder="0"
                        />
                    </div>
                    <div class="input-container checkbox">
                        <div class="checkbox-section">
                            <app-checkbox v-model="subscriptionPlan.hasContactLimit" />
                            <label>Contact Limit?</label>
                        </div>
                    </div>
                    <div v-if="subscriptionPlan.hasContactLimit" class="input-container indented">
                        <app-input
                            v-model="subscriptionPlan.contactLimit"
                            label="Contact Limit"
                            placeholder="0"
                        />
                    </div>
                    <div class="input-container checkbox">
                        <div class="checkbox-section">
                            <app-checkbox v-model="subscriptionPlan.hasUserLimit" />
                            <label>User Limit?</label>
                        </div>
                    </div>
                    <div v-if="subscriptionPlan.hasUserLimit" class="input-container indented">
                        <app-input
                            v-model="subscriptionPlan.userLimit"
                            label="User Limit"
                            placeholder="0"
                        />
                    </div>
                    <div class="input-container checkbox">
                        <div class="checkbox-section">
                            <app-checkbox v-model="subscriptionPlan.hasUserPricing" />
                            <label>User Pricing?</label>
                        </div>
                    </div>
                    <div
                        v-if="subscriptionPlan.hasUserPricing"
                        class="input-container indented free-user"
                    >
                        <app-input
                            v-model="subscriptionPlan.freeUsers"
                            label="Free Users"
                            placeholder="0"
                        />
                    </div>
                    <div v-if="subscriptionPlan.hasUserPricing" class="input-container indented">
                        <app-input
                            v-model="subscriptionPlan.userPrice"
                            label="Price per User"
                            placeholder="0"
                        />
                    </div>
                    <div class="input-container">
                        <label>Features</label>
                        <div class="features-container">
                            <div v-for="feature in features" :key="feature.id" class="feature">
                                <app-switch v-model="feature.enabled">
                                    {{ feature.name }}
                                </app-switch>
                            </div>
                        </div>
                    </div>
                    <div class="templates-container">
                        <label>Templates</label>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ graphicalTemplateCount }}</span>
                                    Graphical Emails
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearGraphicalTemplates()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ nonGraphicalTemplateCount }}</span>
                                    Non-Graphical Emails
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openNonGraphicalTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button
                                    class="delete-btn"
                                    @click="clearNonGraphicalTemplates()"
                                >
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ collateralCount }}</span> Collaterals
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openCollateralSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearCollateral()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ collateralBuildCount }}</span> Collateral
                                    Builds
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openCollateralBuildSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearCollateralBuilds()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ marketingPlanTemplateCount }}</span>
                                    Marketing Plans
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openMarketingPlanTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button
                                    class="delete-btn"
                                    @click="clearMarketingPlanTemplates()"
                                >
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ journeyTemplateCount }}</span>
                                    Journeys
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openJourneyTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearJourneyTemplates()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ landingPageTemplateCount }}</span>
                                    Landing Pages
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openLandingPageTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearLandingPageTemplates()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ landingPageFormCount }}</span>
                                    Forms
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openLandingPageFormSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearLandingPageForms()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ documentTemplateCount }}</span>
                                    Document Builder Templates
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openDocumentTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearDocumentTemplates()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ socialPostTemplateCount }}</span>
                                    Social Media Posts
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openSocialPostTemplateSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearSocialPostTemplates()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ headerCount }}</span>
                                    Headers
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openHeaderSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearHeaders()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                        <div class="template-type">
                            <div class="template-name-container">
                                <p>
                                    <span class="count">{{ footerCount }}</span>
                                    Footers
                                </p>
                            </div>
                            <div class="template-options">
                                <app-button @click="openFooterSelectWidget()">
                                    <r7-edit-svg />
                                </app-button>
                                <app-button class="delete-btn" @click="clearFooters()">
                                    <s4-delete-svg />
                                </app-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppSwitch = () => import('@/components/common/app-switch');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const R7EditSvg = () => import('@/components/svg/grid/r7-edit-svg');
const S4DeleteSvg = () => import('@/components/svg/grid/s4-delete-svg');

import EventBus from '@/scripts/event-bus.js';
import { SocialMediaSharedAssetsService } from '@/scripts/services/social-media/social-media-shared-assets-service';
import { SocialPostTemplateBoardServiceMixin } from '@/mixins/board/social-post-template-board-service-mixin';

export default {
    name: 'subscription-plan-widget',

    components: {
        'app-button': AppButton,
        'app-switch': AppSwitch,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'loading-screen-svg': LoadingScreenSvg,
        'app-checkbox': AppCheckbox,
        's4-delete-svg': S4DeleteSvg,
        'r7-edit-svg': R7EditSvg,
    },

    mixins: [SocialPostTemplateBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            subscriptionPlan: {
                id: this.widget.subscriptionPlanId,
                name: null,
                billingCycle: null,
                basePrice: null,
                contactLimit: null,
                userPrice: null,
                userLimit: null,
                hasUserPricing: null,
                hasUserLimit: null,
                hasContactLimit: null,
                freeUsers: 0,
                graphicalTemplateIds: [],
                nonGraphicalTemplateIds: [],
                collateralIds: [],
                collateralBuildIds: [],
                marketingPlanTemplateIds: [],
                journeyTemplateIds: [],
                landingPageTemplateIds: [],
                formIds: [],
                documentTemplateIds: [],
                socialPostTemplateIds: [],
                headerIds: [],
                footerIds: [],
            },

            features: [],
        };
    },

    computed: {
        graphicalTemplateCount() {
            return this.subscriptionPlan.graphicalTemplateIds.length;
        },

        nonGraphicalTemplateCount() {
            return this.subscriptionPlan.nonGraphicalTemplateIds.length;
        },

        marketingPlanTemplateCount() {
            return this.subscriptionPlan.marketingPlanTemplateIds.length;
        },

        journeyTemplateCount() {
            return this.subscriptionPlan.journeyTemplateIds.length;
        },

        landingPageTemplateCount() {
            return this.subscriptionPlan.landingPageTemplateIds.length;
        },

        landingPageFormCount() {
            return this.subscriptionPlan.formIds.length;
        },

        collateralCount() {
            return this.subscriptionPlan.collateralIds.length;
        },

        collateralBuildCount() {
            return this.subscriptionPlan.collateralBuildIds.length;
        },

        documentTemplateCount() {
            return this.subscriptionPlan.documentTemplateIds.length;
        },

        socialPostTemplateCount() {
            return this.subscriptionPlan.socialPostTemplateIds.length;
        },

        headerCount() {
            return this.subscriptionPlan.headerIds.length;
        },

        footerCount() {
            return this.subscriptionPlan.footerIds.length;
        },
    },

    mounted() {
        EventBus.$on('template:select', this.setSelectedGraphicalTemplates);
        EventBus.$on('email-template:select', this.setSelectedNonGraphicalTemplates);
        EventBus.$on('marketing-plan-template:select', this.setSelectedMarketingPlanTemplates);
        EventBus.$on('collateral:select', this.setSelectedCollateral);
        EventBus.$on('collateral-build:select', this.setSelectedCollateralBuilds);
        EventBus.$on('journey-template:select', this.setSelectedJourneyTemplates);
        EventBus.$on('landing-page-template:select', this.setSelectedLandingPageTemplates);
        EventBus.$on('landing-page-form:select', this.setSelectedLandingPageForms);
        EventBus.$on('document-template:select', this.setSelectedDocumentTemplates);
        EventBus.$on('social-post-template:select', this.setSelectedSocialPostTemplates);
        EventBus.$on('header:select', this.setSelectedHeaders);
        EventBus.$on('footer:select', this.setSelectedFooters);
    },

    created() {
        this.getFeatures().then(() => {
            if (this.subscriptionPlan.id) {
                this.loading = true;
                this.getSubscriptionPlan()
                    .then(this.getSocialPostTemplateIds)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your subscription plan.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            } else {
                this.loading = false;
            }
        });
    },

    beforeDestroy() {
        EventBus.$off('template:select', this.setSelectedGraphicalTemplates);
        EventBus.$off('email-template:select', this.setSelectedNonGraphicalTemplates);
        EventBus.$off('marketing-plan-template:select', this.setSelectedMarketingPlanTemplates);
        EventBus.$off('collateral:select', this.setSelectedCollateral);
        EventBus.$off('collateral-build:select', this.setSelectedCollateralBuilds);
        EventBus.$off('landing-page-template:select', this.setSelectedLandingPageTemplates);
        EventBus.$off('landing-page-form:select', this.setSelectedLandingPageForms);
        EventBus.$off('document-template:select', this.setSelectedDocumentTemplates);
        EventBus.$off('social-post-template:select', this.setSelectedSocialPostTemplates);
        EventBus.$off('header:select', this.setSelectedHeaders);
        EventBus.$off('footer:select', this.setSelectedFooters);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            if (this.subscriptionPlan.id) {
                this.patch();
            } else {
                this.post();
            }
        },

        patch() {
            this.loading = true;

            var featureToggles = this.features
                .filter((f) => f.enabled)
                .map((f) => {
                    return {
                        featureId: f.id,
                        enabled: f.enabled,
                    };
                });

            var changes = [
                {
                    name: 'Name',
                    value: this.subscriptionPlan.name,
                },
                {
                    name: 'BasePrice',
                    value: this.subscriptionPlan.basePrice,
                },
                {
                    name: 'BillingCycle',
                    value: this.subscriptionPlan.billingCycle,
                },
                {
                    name: 'ContactLimit',
                    value: this.subscriptionPlan.hasContactLimit
                        ? this.subscriptionPlan.contactLimit
                        : null,
                },
                {
                    name: 'UserLimit',
                    value: this.subscriptionPlan.hasUserLimit
                        ? this.subscriptionPlan.userLimit
                        : null,
                },
                {
                    name: 'UserPrice',
                    value: this.subscriptionPlan.hasUserPricing
                        ? this.subscriptionPlan.userPrice
                        : null,
                },
                {
                    name: 'FreeUsers',
                    value: this.subscriptionPlan.hasUserPricing
                        ? this.subscriptionPlan.freeUsers
                        : null,
                },
                {
                    name: 'FeatureToggles',
                    value: featureToggles,
                },
                {
                    name: 'TemplateIds',
                    value: this.subscriptionPlan.graphicalTemplateIds,
                },
                {
                    name: 'EmailTemplateIds',
                    value: this.subscriptionPlan.nonGraphicalTemplateIds,
                },
                {
                    name: 'CollateralIds',
                    value: this.subscriptionPlan.collateralIds,
                },
                {
                    name: 'CollateralBuildIds',
                    value: this.subscriptionPlan.collateralBuildIds,
                },
                {
                    name: 'MarketingPlanTemplateIds',
                    value: this.subscriptionPlan.marketingPlanTemplateIds,
                },
                {
                    name: 'JourneyTemplateIds',
                    value: this.subscriptionPlan.journeyTemplateIds,
                },
                {
                    name: 'LandingPageTemplateIds',
                    value: this.subscriptionPlan.landingPageTemplateIds,
                },
                {
                    name: 'FormIds',
                    value: this.subscriptionPlan.formIds,
                },
                {
                    name: 'DocumentTemplateIds',
                    value: this.subscriptionPlan.documentTemplateIds,
                },
                {
                    name: 'LandingPageSectionIds',
                    value: this.subscriptionPlan.headerIds.concat(this.subscriptionPlan.footerIds),
                },
            ];
            this.$api
                .patch(`/api/Promos/${this.subscriptionPlan.id}`, changes)
                .then(() =>
                    SocialMediaSharedAssetsService.patch(null, {
                        PostTemplateIds: this.subscriptionPlan.socialPostTemplateIds,
                        PromoId: this.subscriptionPlan.id,
                    }),
                )
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your subscription plan has been successfully updated.',
                    );
                    EventBus.$emit('subscription-plan:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your subscription plan.',
                        { error },
                    );
                });
        },

        post() {
            this.loading = true;

            var featureToggles = this.features
                .filter((f) => f.enabled)
                .map((f) => {
                    return {
                        featureId: f.id,
                        enabled: f.enabled,
                    };
                });

            var subscriptionPlan = {
                Name: this.subscriptionPlan.name,
                BasePrice: this.subscriptionPlan.basePrice,
                BillingCycle: this.subscriptionPlan.billingCycle,
                ContactLimit: this.subscriptionPlan.hasContactLimit
                    ? this.subscriptionPlan.contactLimit
                    : null,

                UserLimit: this.subscriptionPlan.hasUserLimit
                    ? this.subscriptionPlan.userLimit
                    : null,

                UserPrice: this.subscriptionPlan.hasUserPricing
                    ? this.subscriptionPlan.userPrice
                    : null,

                FreeUsers: this.subscriptionPlan.hasUserPricing
                    ? this.subscriptionPlan.freeUsers
                    : null,

                FeatureToggles: featureToggles,
                TemplateIds: this.subscriptionPlan.graphicalTemplateIds,
                EmailTemplateIds: this.subscriptionPlan.nonGraphicalTemplateIds,
                CollateralIds: this.subscriptionPlan.collateralIds,
                CollateralBuildIds: this.subscriptionPlan.collateralBuildIds,
                MarketingPlanTemplateIds: this.subscriptionPlan.marketingPlanTemplateIds,
                JourneyTemplateIds: this.subscriptionPlan.journeyTemplateIds,
                LandingPageTemplateIds: this.subscriptionPlan.landingPageTemplateIds,
                FormIds: this.subscriptionPlan.formIds,
                DocumentTemplateIds: this.subscriptionPlan.documentTemplateIds,
                LandingPageSectionIds: this.subscriptionPlan.headerIds.concat(
                    this.subscriptionPlan.footerIds,
                ),
            };

            this.$api
                .post('/api/Promos', subscriptionPlan)
                .then(({ data: { Id } }) =>
                    SocialMediaSharedAssetsService.post(null, {
                        PostTemplateIds: this.subscriptionPlan.socialPostTemplateIds,
                        PromoId: Id,
                    }),
                )
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your subscription plan has been successfully created.',
                    );
                    EventBus.$emit('subscription-plan:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your subscription plan.',
                        { error },
                    );
                });
        },

        getSocialPostTemplateIds() {
            return new Promise((resolve, reject) => {
                const options = {
                    promoId: this.subscriptionPlan.id,
                    type: 'published',
                };

                this.getPromosData()
                    .then(({ items }) => {
                        options.promoIds = items;

                        return this.getSocialPostTemplates(options, null);
                    })
                    .then(({ items }) => {
                        this.subscriptionPlan.socialPostTemplateIds = items.map(({ id }) => id);
                        resolve();
                    })
                    .catch(reject);
            });
        },

        getSubscriptionPlan() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Promos?$filter=Id eq ${this.subscriptionPlan.id}&$expand=FeatureToggles($select=FeatureId,Enabled),SharedAssets($select=MarketingPlanTemplateId,TemplateId,EmailTemplateId,CollateralId,CollateralBuildId,JourneyTemplateId,LandingPageTemplateId,FormId,DocumentTemplateId;$expand=LandingPageSection)`,
                    )
                    .then((response) => {
                        this.subscriptionPlan.name = response.data.value[0].Name;
                        this.subscriptionPlan.billingCycle = response.data.value[0].BillingCycle;
                        this.subscriptionPlan.basePrice = response.data.value[0].BasePrice;
                        this.subscriptionPlan.contactLimit = response.data.value[0].ContactLimit;
                        this.subscriptionPlan.userLimit = response.data.value[0].UserLimit;
                        this.subscriptionPlan.freeUsers = response.data.value[0].FreeUsers;
                        this.subscriptionPlan.userPrice = response.data.value[0].UserPrice;
                        this.subscriptionPlan.hasUserPricing =
                            response.data.value[0].UserPrice !== null;
                        this.subscriptionPlan.hasUserLimit =
                            response.data.value[0].UserLimit !== null;
                        this.subscriptionPlan.hasContactLimit =
                            response.data.value[0].ContactLimit !== null;

                        this.subscriptionPlan.graphicalTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.TemplateId,
                            ).map((asset) => asset.TemplateId);

                        this.subscriptionPlan.nonGraphicalTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.EmailTemplateId,
                            ).map((asset) => asset.EmailTemplateId);

                        this.subscriptionPlan.collateralIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.CollateralId,
                            ).map((asset) => asset.CollateralId);

                        this.subscriptionPlan.collateralBuildIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.CollateralBuildId,
                            ).map((asset) => asset.CollateralBuildId);

                        this.subscriptionPlan.marketingPlanTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.MarketingPlanTemplateId,
                            ).map((asset) => asset.MarketingPlanTemplateId);

                        this.subscriptionPlan.journeyTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.JourneyTemplateId,
                            ).map((asset) => asset.JourneyTemplateId);

                        this.subscriptionPlan.landingPageTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.LandingPageTemplateId,
                            ).map((asset) => asset.LandingPageTemplateId);

                        this.subscriptionPlan.formIds = response.data.value[0].SharedAssets.filter(
                            (asset) => asset.FormId,
                        ).map((asset) => asset.FormId);

                        this.subscriptionPlan.documentTemplateIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) => asset.DocumentTemplateId,
                            ).map((asset) => asset.DocumentTemplateId);

                        this.subscriptionPlan.headerIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) =>
                                    asset.LandingPageSection &&
                                    asset.LandingPageSection.Type === 'Header',
                            ).map((asset) => asset.LandingPageSection.Id);

                        this.subscriptionPlan.footerIds =
                            response.data.value[0].SharedAssets.filter(
                                (asset) =>
                                    asset.LandingPageSection &&
                                    asset.LandingPageSection.Type === 'Footer',
                            ).map((asset) => asset.LandingPageSection.Id);

                        this.features.forEach((feature) => {
                            feature.enabled = response.data.value[0].FeatureToggles.some(
                                (t) => t.FeatureId === feature.id && t.Enabled,
                            );
                        });

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your subscription plan.',
                            { error },
                        );

                        reject();
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            });
        },

        getFeatures() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get('/service/Features')
                    .then((response) => {
                        this.features = response.data.value.map((item) => {
                            return {
                                id: item.Id,
                                name: item.Name,
                                enabled: false,
                            };
                        });

                        resolve();
                    })
                    .catch(reject);
            });
        },

        openNonGraphicalTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'non-graphical-email-template-select-widget',
                initialSelectedIds: this.subscriptionPlan.nonGraphicalTemplateIds,
            });
        },

        openTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'graphical-email-template-select-widget',
                initialSelectedIds: this.subscriptionPlan.graphicalTemplateIds,
            });
        },

        openCollateralSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'collateral-organization-select-widget',
                initialSelectedIds: this.subscriptionPlan.collateralIds,
            });
        },

        openCollateralBuildSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'collateral-build-select-widget',
                initialSelectedIds: this.subscriptionPlan.collateralBuildIds,
            });
        },

        openMarketingPlanTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'marketing-plan-template-select-widget',
                allowMultipleSelections: true,
                initialSelectedIds: this.subscriptionPlan.marketingPlanTemplateIds,
            });
        },

        openJourneyTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'journey-template-select-widget',
                allowMultipleSelections: true,
                initialSelectedIds: this.subscriptionPlan.journeyTemplateIds,
            });
        },

        openLandingPageTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'landing-page-template-select-widget',
                initialSelectedIds: this.subscriptionPlan.landingPageTemplateIds,
            });
        },

        openLandingPageFormSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'landing-page-form-select-widget',
                initialSelectedIds: this.subscriptionPlan.formIds,
            });
        },

        openDocumentTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'document-template-select-widget',
                initialSelectedIds: this.subscriptionPlan.documentTemplateIds,
            });
        },

        openSocialPostTemplateSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'social-post-template-select-widget',
                initialSelectedIds: this.subscriptionPlan.socialPostTemplateIds,
            });
        },

        openHeaderSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'header-select-widget',
                initialSelectedIds: this.subscriptionPlan.headerIds,
            });
        },

        openFooterSelectWidget() {
            EventBus.$emit('open-widget', {
                name: 'footer-select-widget',
                initialSelectedIds: this.subscriptionPlan.footerIds,
            });
        },

        setSelectedGraphicalTemplates(graphicalTemplateIds) {
            this.subscriptionPlan.graphicalTemplateIds = graphicalTemplateIds;
        },

        setSelectedNonGraphicalTemplates(nonGraphicalTemplateIds) {
            this.subscriptionPlan.nonGraphicalTemplateIds = nonGraphicalTemplateIds;
        },

        setSelectedCollateral(collateralIds) {
            this.subscriptionPlan.collateralIds = collateralIds;
        },

        setSelectedCollateralBuilds(collateralBuildIds) {
            this.subscriptionPlan.collateralBuildIds = collateralBuildIds;
        },

        setSelectedMarketingPlanTemplates(marketingPlanTemplateIds) {
            this.subscriptionPlan.marketingPlanTemplateIds = marketingPlanTemplateIds;
        },

        setSelectedJourneyTemplates(journeyTemplateIds) {
            this.subscriptionPlan.journeyTemplateIds = journeyTemplateIds;
        },

        setSelectedLandingPageTemplates(landingPageTemplateIds) {
            this.subscriptionPlan.landingPageTemplateIds = landingPageTemplateIds;
        },

        setSelectedLandingPageForms(formIds) {
            this.subscriptionPlan.formIds = formIds;
        },

        setSelectedDocumentTemplates(documentTemplateIds) {
            this.subscriptionPlan.documentTemplateIds = documentTemplateIds;
        },

        setSelectedSocialPostTemplates(socialPostTemplateIds) {
            this.subscriptionPlan.socialPostTemplateIds = socialPostTemplateIds;
        },

        setSelectedHeaders(headerIds) {
            this.subscriptionPlan.headerIds = headerIds;
        },

        setSelectedFooters(footerIds) {
            this.subscriptionPlan.footerIds = footerIds;
        },

        clearGraphicalTemplates() {
            this.subscriptionPlan.graphicalTemplateIds = [];
        },

        clearNonGraphicalTemplates() {
            this.subscriptionPlan.nonGraphicalTemplateIds = [];
        },

        clearCollateral() {
            this.subscriptionPlan.collateralIds = [];
        },

        clearCollateralBuilds() {
            this.subscriptionPlan.collateralBuildIds = [];
        },

        clearMarketingPlanTemplates() {
            this.subscriptionPlan.marketingPlanTemplateIds = [];
        },

        clearJourneyTemplates() {
            this.subscriptionPlan.journeyTemplateIds = [];
        },

        clearLandingPageTemplates() {
            this.subscriptionPlan.landingPageTemplateIds = [];
        },

        clearLandingPageForms() {
            this.subscriptionPlan.formIds = [];
        },

        clearDocumentTemplates() {
            this.subscriptionPlan.documentTemplateIds = [];
        },

        clearSocialPostTemplates() {
            this.subscriptionPlan.socialPostTemplateIds = [];
        },

        clearHeaders() {
            this.subscriptionPlan.headerIds = [];
        },

        clearFooters() {
            this.subscriptionPlan.footerIds = [];
        },
    },
};
</script>

<style>
.subscription-plan-widget .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.subscription-plan-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.subscription-plan-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.subscription-plan-widget label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 0.5em;
}

.subscription-plan-widget .input-container {
    width: 100%;
    margin-bottom: 1.25em;
    background: #fff;
    z-index: 10;
    position: relative;
}

.subscription-plan-widget .checkbox-section {
    display: flex;
    align-items: center;
}

.subscription-plan-widget .checkbox-section .app-checkbox {
    margin-right: 10px;
}

.subscription-plan-widget .checkbox-section label {
    margin: 0;
    font-weight: 500;
}

.subscription-plan-widget .app-switch {
    justify-content: flex-start !important;
}

.subscription-plan-widget .template-type {
    display: flex;
    margin-bottom: 10px;
}

.subscription-plan-widget .template-type .template-name-container {
    background: #fafafa;
    font: normal 16px/1em 'Roboto';
    width: 100%;
    border: 2px solid #cac6cb;
    padding: 7px 15px;
    border-radius: 20px;
    color: #382153;
    outline: none;
    width: 80%;
    padding-left: 20px;
}

.subscription-plan-widget .template-type .template-name-container p {
    margin: 0;
}

.subscription-plan-widget .template-type .template-name-container .count {
    font-weight: 600;
}

.subscription-plan-widget .r7-edit-svg path,
.subscription-plan-widget .s4-delete-svg path,
.subscription-plan-widget .s4-delete-svg rect {
    fill: #fff;
}

.subscription-plan-widget .s4-delete-svg {
    width: 19px;
}

.subscription-plan-widget .template-type .template-options {
    display: flex;
}

.subscription-plan-widget .template-type .template-options button {
    padding: 0 10px;
    margin-left: 10px;
    height: initial;
    width: 35px;
    height: 35px;
}

.subscription-plan-widget .delete-btn {
    background: #f30070;
}

.subscription-plan-widget .delete-btn:hover {
    background: #c80768;
}

.subscription-plan-widget .template-name-container {
    position: relative;
    background: #fff;
}

.subscription-plan-widget .indented {
    position: relative;
    z-index: 2;
}

.subscription-plan-widget .indented .app-input {
    padding-left: 30px;
    background: #fff;
}

.subscription-plan-widget .indented::before {
    position: absolute;
    top: -30px;
    left: 10px;
    border-left: 2px solid #cac6cb;
    border-bottom: 2px solid #cac6cb;
    border-radius: 10px;
    height: 50px;
    width: 25px;
    content: '';
    clip: rect(0px, 14px, 50px, 0px);
}

.subscription-plan-widget .input-container.indented::before {
    top: -30px;
    height: 80px;
    clip: rect(0px, 14px, 80px, 0px);
    z-index: 2;
}

.subscription-plan-widget .input-container.indented .checkbox-section {
    padding: 0 0 5px;
}

.subscription-plan-widget .input-container.checkbox {
    background: #fff;
    padding-bottom: 5px;
    padding-left: 0px;
    margin-bottom: 10px;
}

.subscription-plan-widget .features-container {
    display: flex;
    flex-wrap: wrap;
}

.subscription-plan-widget .feature {
    max-width: 50%;
    width: 100%;
}

.subscription-plan-widget .feature .app-switch .app-switch-container {
    width: 71px;
    height: 25px;
}
.subscription-plan-widget .feature .app-switch.app-switch-checked .app-switch-thumb {
    width: 25px;
    height: 25px;
    transform: translate3d(47px, 0, 0);
}

.subscription-plan-widget .feature .app-switch .app-switch-container .app-switch-value-text {
    font-size: 14px;
    left: 30px;
    top: 3px;
}

.subscription-plan-widget
    .feature
    .app-switch.app-switch-checked
    .app-switch-container
    .app-switch-value-text {
    left: 12px;
}

.subscription-plan-widget .free-user {
    z-index: 3;
}
</style>
